import axios, { AxiosResponse } from 'axios';
import { parseJSON } from '.';
import {
	TargetUrlResponse,
	Airport,
	FlightSearchRequest,
	FlyDriveDestinations,
	CJSSearchRequest,
	CJSSearchResponse,
	LocationParams,
	CJSLocationSearchRequest,
	CJSLocationSearchResponse,
	SalesBannerRequest,
	SalesBannerLocation,
	SalesBannerResponse
} from '../../types/api';

export async function getAirportByIATA(
	code: string,
	basePath?: string
): Promise<AxiosResponse<Airport>> {
	const url = `${basePath ? basePath : ''}/travelplus/search-panel-api/airports/by-code?iataCode=${code}`;

	const response: AxiosResponse<Airport> = await axios({
		method: 'get',
		url: url,
		responseType: 'json'
	});

	return response;
}

export async function getAirportsByCity(
	code: string,
	basePath?: string
): Promise<AxiosResponse<Airport[]>> {
	const url = `${basePath ? basePath : ''}/travelplus/search-panel-api/airports/predictive/by-city?cityTerm=${code}`;

	const response: AxiosResponse<Airport[]> = await axios({
		method: 'get',
		url: url,
		responseType: 'json'
	});

	return response;
}

export async function getAirportsByTerm(
	term: string,
	basePath?: string
): Promise<AxiosResponse<Airport[]>> {
	const url = `${basePath ? basePath : ''}/travelplus/search-panel-api/airports/predictive/by-term?term=${term}`;

	const response: AxiosResponse<Airport[]> = await axios({
		method: 'get',
		url: url,
		responseType: 'json'
	});

	return response;
}

export async function getFlightSearchUrl(
	query: FlightSearchRequest
): Promise<AxiosResponse<TargetUrlResponse>> {
	const params = {
		searchType: query.searchType,
		passengers: query.passengers,
		...(query.cabin && { cabin: query.cabin }),
		...(query.options && { options: query.options }),
		...(query.discountCode && { discountCode: query.discountCode })
	};

	const slices = query.origin.map((origin, index) => {
		return {
			origin: origin,
			destination: query?.destination?.[index],
			departing: query?.departing?.[index]
		};
	});

	const url = `/travelplus/search-panel-api/get-target-search-url?${slices
		.map(
			(slice) =>
				`origin=${slice.origin}&destination=${slice.destination}&departing=${slice.departing}`
		)
		.join('&')}&${Object.entries(params)
		.map(([key, value]) => `${key}=${value}`)
		.join('&')}`;

	const response: AxiosResponse<TargetUrlResponse> = await axios({
		method: 'get',
		url: url,
		responseType: 'json'
	});

	return response;
}

export async function getCJSSearchUrl(
	query: CJSSearchRequest
): Promise<AxiosResponse<TargetUrlResponse>> {
	const params = new URLSearchParams();

	params.set('brand', query.brand);
	params.set('submissionType', query.submissionType);
	params.set('location', query.location);
	params.set('duration', String(query.duration));
	params.set('departureDate', query.departureDate);

	if (query.gateway) params.set('gateway', query.gateway);
	if (query.direct) params.set('direct', 'true');
	if (query.returnDate) params.set('returnDate', query.returnDate);
	if (query.pickUp) params.set('pickUp', query.pickUp);
	if (query.dropOff) params.set('dropOff', query.dropOff);
	if (query.pickUpTime) params.set('pickUpTime', query.pickUpTime);
	if (query.dropOffTime) params.set('dropOffTime', query.dropOffTime);

	for (const composition of query.partyCompositions) {
		params.append(
			query.submissionType === 'FLYDRIVE' ? 'passengers' : 'room',
			composition
		);
	}

	const url = `/travelplus/search-panel-api/get-target-flight-plus-search-url?${params.toString()}`;

	const response: AxiosResponse<TargetUrlResponse> = await axios.get(url, {
		responseType: 'json'
	});

	return response;
}

export async function getCJSSearchResponse(
	location: string,
	params: LocationParams,
	basePath?: string
): Promise<AxiosResponse<CJSSearchResponse>> {
	const url = `${basePath ? basePath : ''}/travelplus/search-panel-api/location/${location}?${Object.entries(
		params
	)
		.map(([key, value]) => `${key}=${value}`)
		.join('&')}`;

	const response: AxiosResponse<CJSSearchResponse> = await axios({
		method: 'get',
		url: url,
		responseType: 'json'
	});

	return response;
}

export async function getCJSLocationSearchUrl(
	query: CJSLocationSearchRequest
): Promise<AxiosResponse<CJSLocationSearchResponse>> {
	const params = {
		brand: query.brand,
		bookingType: query.bookingType,
		q: query.query,
		searchableOnly: query.searchableOnly || true,
		...(query.level && { level: query.level }),
		...(query.parent && { parent: query.parent }),
		...(query.locationId && { locationId: query.locationId }),
		...(query.resultSize && { resultSize: query.resultSize }),
		...(query.locationType && { locationType: query.locationType }),
		...(query.iataCode && { iataCode: query.iataCode }),
		...(query.departureGatewayId && {
			departureGatewayId: query.departureGatewayId
		}),
		...(query.withEcmsId && { withECMSId: query.withEcmsId }),
		...(query.ecmsLocationId && { ECMSLocationId: query.ecmsLocationId })
	};

	const url = `/travelplus/search-panel-api/locations/search?${Object.entries(
		params
	)
		.map(([key, value]) => `${key}=${value}`)
		.join('&')}`;

	const response: AxiosResponse<CJSLocationSearchResponse> = await axios({
		method: 'get',
		url: url,
		responseType: 'json'
	});

	return response;
}

export async function getFlydriveDestinations(): Promise<
	AxiosResponse<FlyDriveDestinations>
> {
	const url = `/travelplus/search-panel-api/flydrive/destinations?brand=VHOLS`;
	const response: AxiosResponse<FlyDriveDestinations> = await axios({
		method: 'get',
		url: url,
		responseType: 'json'
	});

	return response;
}

export async function getMulticentreDestinations(): Promise<
	AxiosResponse<FlyDriveDestinations>
> {
	const url = `/travelplus/search-panel-api/multicentre/destinations?brand=VHOLS
`;
	const response: AxiosResponse<FlyDriveDestinations> = await axios({
		method: 'get',
		url: url,
		responseType: 'json'
	});

	return response;
}

export async function getSalesBanners(
	location: SalesBannerLocation,
	request: SalesBannerRequest
): Promise<AxiosResponse<SalesBannerResponse>> {
	const url = parseJSON(window?.GqlEndpoint);

	if (!url) {
		return null;
	}

	const {
		bookingType = '',
		originAirport,
		destinationAirport = null,
		departureDate = null,
		returnDate = null,
		flightDirection = '',
		numAdults = 0,
		numTeens = 0,
		numChildren = 0,
		numInfants = 0,
		destinationURL = null
	} = request;

	const getQuery = `
		query GetSalesBanners(
			$displayLocations: [String!]!,
			$request: SalesBannerRequestInput!,
		) {
			salesBanners(
				displayLocations: $displayLocations,
				request: $request,
			) {
				bodyHtml
				bodyPlainText
				displayLocation
				title
				theme
				image
				imageAltText
			}
		}
	`;

	const getQueryVariables = {
		displayLocations: [location],
		request: {
			bookingType,
			originAirport,
			...(destinationAirport && { destinationAirport: destinationAirport }),
			departureDate: departureDate
				? departureDate.toISOString().split('T')[0]
				: null,
			returnDate: returnDate ? returnDate.toISOString().split('T')[0] : null,
			flightDirection,
			numAdults,
			numTeens,
			numChildren,
			numInfants,
			...(destinationURL && { destinationURL: destinationURL })
		}
	};

	try {
		const response: AxiosResponse<SalesBannerResponse> = await axios({
			method: 'post',
			url: url,
			data: {
				query: getQuery,
				variables: getQueryVariables
			},
			responseType: 'json',
			headers: {
				accept: '*/*',
				'Content-Type': 'application/json; charset=UTF-8'
			}
		});

		return response;
	} catch (error) {
		throw new Error(`Failed to fetch sales banners: ${error}`);
	}
}
